import { FC, PropsWithChildren } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { setDrawerOpen } from 'store/drawerSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';

import { RaaSpinner } from 'raa-react';

import { TextButton } from 'components/sharedElements/sharedElements';

import classNames from 'classnames';

export const DrawerBody: FC<PropsWithChildren> = ({ children }) => (
  <div className="tw-flex-grow tw-divide-y tw-divide-raa-gray-3 tw-overflow-y-auto tw-overscroll-y-contain">
    {children}
  </div>
);

export const DrawerContainer: FC<PropsWithChildren> = ({ children }) => (
  <div className="tw-flex tw-h-full tw-flex-col">{children}</div>
);

export const SpinnerContainer: FC<PropsWithChildren<{ showSpinner: boolean; size?: string }>> = ({
  children,
  showSpinner,
  size = '7rem'
}) => (
  <>
    {showSpinner && (
      <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <RaaSpinner size={size} />
      </div>
    )}
    <div className={classNames('tw-h-full tw-w-full', { 'tw-hidden': showSpinner })}>{children}</div>
  </>
);

export const DrawerSection: FC<PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <section className={className ? className : 'tw-p-4 md:tw-p-6'}>{children}</section>
);

export const NavigationDrawerHead: FC<{ disableBackNavigation?: boolean; disableOpenMap?: boolean }> = ({
  disableBackNavigation = false,
  disableOpenMap = false
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <div className="tw-flex tw-px-3 tw-py-2">
      <div className="tw-w-[85px]">
        {location.key !== 'default' && !disableBackNavigation && (
          <TextButton icon="raa-icon-arrow-left" reverse={false} uppercase={true} onClick={() => navigate(-1)}>
            Tillbaka
          </TextButton>
        )}
      </div>

      <div className="tw-flex tw-grow tw-justify-center">
        {!disableOpenMap && (
          <div className="md:tw-hidden">
            <TextButton
              icon="raa-icon-arrow-down"
              uppercase={true}
              onClick={() => dispatch(setDrawerOpen('minimized'))}
            >
              visa karta
            </TextButton>
          </div>
        )}
      </div>

      <TextButton icon="raa-icon-close" reverse={true} uppercase={true} onClick={() => navigate('/')}>
        Stäng
      </TextButton>
    </div>
  );
};

export const TabNavigationDrawerHead: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="tw-flex tw-justify-end tw-py-2">
      <div data-testid="search-tabs" className="raa-button-tabs tw-flex-grow" role="tablist" aria-label="Söktyp">
        <NavLink
          to="/sok-bebyggelseobjekt"
          role="tab"
          className="raa-button"
          aria-selected={location.pathname === '/sok-bebyggelseobjekt'}
          aria-current={location.pathname === '/sok-bebyggelseobjekt' ? 'page' : undefined}
        >
          Bebyggelseobjekt
        </NavLink>

        <NavLink
          to="/sok-inventering"
          role="tab"
          className="raa-button"
          aria-selected={location.pathname === '/sok-inventering'}
          aria-current={location.pathname === '/sok-inventering' ? 'page' : undefined}
        >
          Inventeringar
        </NavLink>
      </div>

      <TextButton
        icon="raa-icon !tw-no-underline raa-icon-close md:tw-mr-2"
        reverse={true}
        uppercase={true}
        absolute={true}
        onClick={() => navigate('/')}
      >
        <span className="tw-hidden md:tw-block">Avbryt</span>
      </TextButton>
    </div>
  );
};

export const DrawerFooter: FC<PropsWithChildren<{ className?: string; bgColorLevel?: number }>> = ({
  children,
  className = '',
  bgColorLevel = 3
}) => (
  <div
    className={classNames(
      'tw-flex tw-justify-center tw-gap-1 tw-px-3 tw-py-3 md:tw-justify-between md:tw-p-3',
      `tw-bg-raa-gray-${bgColorLevel}`,
      className
    )}
  >
    {children}
  </div>
);
